import { FunctionalComponent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { LeftNavigation } from '@/components/LeftNavigation'
import { TopNavigation } from '@/components/TopNavigation'
import { PageHeader } from '@/components/common/PageHeader'
import { PageHeaderProvider, usePageHeader } from '@/hooks/usePageHeader'
import { useCompany } from '@/hooks/useCompany'
import { useToast } from '@/hooks/useToast'
import { routes, Section } from '@/utils/routes'
import { resolveWorkspaceSelection } from '@/utils/workspaceSelection'
import type { ClientWorkspaceSummaryViewDto } from '@/types/company'
import { UserSettingsDrawer } from '@/components/common/UserSettingsDrawer'
import { CrmConnectionAlertBanner } from '@/components/settings/integrations/components/CrmConnectionAlertBanner'

interface WorkspaceShellProps {
  clientCompanyId: string
  workspaceId: string
  activeSection: Section
  detailFallbackSection?: Section
  children: preact.ComponentChildren
}

const PersistentHeader: FunctionalComponent = () => {
  const { header } = usePageHeader()
  if (!header.items.length && !header.actions) return null
  return (
    <div className="max-w-[85rem] mx-auto px-4 sm:px-6 pt-6">
      <PageHeader items={header.items} actions={header.actions} />
    </div>
  )
}

export const WorkspaceShell: FunctionalComponent<WorkspaceShellProps> = ({
  clientCompanyId,
  workspaceId,
  activeSection,
  detailFallbackSection,
  children,
}) => {
  const location = useLocation()
  const { workspaces, currentWorkspaceId, setCurrentWorkspace, authOrganizationId, hasEnabledAuthConnections } = useCompany()
  const { showInfo } = useToast()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false)

  const handleMobileMenuToggle = () => setIsMobileMenuOpen(prev => !prev)

  const handleSectionChange = (section: Section) => {
    if (section === Section.Admin) {
      location.route(`/company/${clientCompanyId}/settings/company`)
      return
    }
    location.route(`/company/${clientCompanyId}/workspace/${workspaceId || currentWorkspaceId}/${section}`)
  }

  const handleWorkspaceChange = (ws: ClientWorkspaceSummaryViewDto) => {
    setCurrentWorkspace(ws.Id)
    const targetSection = detailFallbackSection || activeSection
    location.route(`/company/${clientCompanyId}/workspace/${ws.Id}/${targetSection}`)
  }

  // Check for auth organization setup
  useEffect(() => {
    // Wait for auth organization ID to be loaded (not undefined)
    if (authOrganizationId === undefined) return

    // If company not configured with auth organization OR no enabled auth connections, redirect to settings
    if (authOrganizationId === null || !hasEnabledAuthConnections) {
      showInfo('Setup required', 'This company is not fully configured yet. Redirected to Authentication settings.', 7000)
      location.route(routes.settingsAuthentication(clientCompanyId))
      return
    }
  }, [clientCompanyId, authOrganizationId, hasEnabledAuthConnections])

  // Keep the current workspace on whatever the URL says (ORB-651) — see resolveWorkspaceSelection
  // for why an "is it still valid?" check is not enough.
  useEffect(() => {
    const selection = resolveWorkspaceSelection({ workspaces, routeWorkspaceId: workspaceId, currentWorkspaceId })
    if (selection.action === 'keep') return

    setCurrentWorkspace(selection.workspaceId)
    if (selection.action === 'redirect') {
      location.route(`/company/${clientCompanyId}/workspace/${selection.workspaceId}/${activeSection}`)
    }
  }, [workspaces, currentWorkspaceId, workspaceId, clientCompanyId, activeSection])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex overflow-x-hidden">
      <LeftNavigation
        activeSection={activeSection}
        onSectionChange={(s: string) => { handleSectionChange(s as Section); setIsMobileMenuOpen(false) }}
        isOpen={isMobileMenuOpen}
        onToggle={handleMobileMenuToggle}
        clientCompanyId={clientCompanyId}
        onWorkspaceChange={handleWorkspaceChange}
      />
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <TopNavigation
          onMenuToggle={handleMobileMenuToggle}
          onSettingsClick={() => setIsUserSettingsOpen(true)}
          onWorkspaceChange={handleWorkspaceChange}
        />
        <main className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-800">
          <PageHeaderProvider>
            <PersistentHeader />
            {activeSection === Section.Campaigns && (
              <CrmConnectionAlertBanner clientCompanyId={clientCompanyId} containerClassName="max-w-[85rem] mx-auto px-4 sm:px-6 pt-4" />
            )}
            <div className="max-w-[85rem] mx-auto overflow-hidden">
              {children}
            </div>
          </PageHeaderProvider>
        </main>
      </div>
      <UserSettingsDrawer isOpen={isUserSettingsOpen} onClose={() => setIsUserSettingsOpen(false)} />
    </div>
  )
}
